import React, { useEffect, useState } from 'react';
import { Modal, Box, CardMedia, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';

import axios from 'axios';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';

import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import MDProgress from 'components/MDProgress';

import FileUploadIcon from '@mui/icons-material/FileUpload';

import { uploadMediaService } from 'services/media.service';
import {
  getPropertyTypeById,
  resetPropertyType,
} from 'store/reducers/propertyTypeSlice';

import MediaCategory from '../../../constants/mediaCategory.constants';

const AddPropertyType = ({
  openModal,
  onCloseModal,
  onCreatePropertyType,
  onUpdatePropertyType,
  isEdit,
  selectedPropertyType,
}) => {
  const dispatch = useDispatch();
  const propertyTypeData = useSelector(
    (state) => state.propertyType.propertyTypeData
  );

  const propertyTypeSchema = yup.object({
    name: yup.string().required(`Property Type name is required`),
    image: yup.string().required(`Image is required`),
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitting, dirtyFields },
    reset,
    setValue,
  } = useForm({
    mode: 'all',
    resolver: yupResolver(propertyTypeSchema),
  });

  const [isLoading, setLoading] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileURL, setSelectedFileURL] = useState('');

  const [uploadProgress, setUploadProgress] = useState(100);

  async function fetchPropertyType() {
    try {
      setLoading(true);
      await dispatch(getPropertyTypeById(selectedPropertyType?.propertyTypeId));
    } catch (error) {
      console.log({ error });
      onCloseModal();
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isEdit && selectedPropertyType?.propertyTypeId) fetchPropertyType();
  }, [isEdit, selectedPropertyType]);

  useEffect(() => {
    if (propertyTypeData) {
      reset(
        {
          name: propertyTypeData?.name,
          image: propertyTypeData?.image,
        },
        {
          keepIsValid: false,
          keepErrors: true,
          keepDirty: false,
        }
      );
      setSelectedFileURL(propertyTypeData?.imageUrl);
    }
  }, [propertyTypeData]);

  useEffect(() => {
    return () => {
      dispatch(resetPropertyType());
    };
  }, []);

  const onAddPropertyTypeSubmit = async (data) => {
    if (!selectedFile) return;
    try {
      const preSignedRes = await uploadMediaService({
        mediaName: selectedFile?.name,
        mediaCategory: MediaCategory.PROPERTY_TYPE,
        mediaType: selectedFile?.name?.split('.')?.pop()?.toLowerCase(),
        // mediaType: selectedFile?.type,
      });
      if (!preSignedRes?.data?.isError) {
        const preSignedURL = preSignedRes?.data?.data?.url;
        const storagePath = preSignedRes?.data?.data?.storagePath;
        const formData = new FormData();
        formData.append('Content-Type', selectedFile?.type);
        formData.append('file', selectedFile);
        // const binaryFile = await selectedFile.arrayBuffer();

        const uploadImage = await axios.put(
          preSignedURL,
          selectedFile,
          // binaryFile,
          {
            headers: {
              // 'content-type': selectedFile?.name?.split('.')?.pop(),
              'content-type': selectedFile?.type,
              'Access-Control-Allow-Origin': '*',
              // 'content-type': 'multipart/form-data',
            },
            onUploadProgress: (axiosProgressEvent) => {
              const progress = Math.round(
                (axiosProgressEvent.loaded / axiosProgressEvent.total) * 100
              );
              setUploadProgress(progress);
            },
          }
        );

        // if upload image is successful
        if (uploadImage.status === 200) {
          onCreatePropertyType(data?.name, storagePath);
        }
      }
    } catch (error) {
      console.log({ error });
      toast.error(
        `${error?.response?.data?.message?.[0] ?? 'Something Went Wrong!'} `
      );
    }
  };

  const onUpdatePropertyTypeSubmit = async (data) => {
    try {
      if (selectedFile) {
        const preSignedRes = await uploadMediaService({
          mediaName: selectedFile?.name,
          mediaCategory: MediaCategory.PROPERTY_TYPE,
          mediaType: selectedFile?.name?.split('.')?.pop()?.toLowerCase(),
        });
        if (!preSignedRes?.data?.isError) {
          const preSignedURL = preSignedRes?.data?.data?.url;
          const storagePath = preSignedRes?.data?.data?.storagePath;
          const formData = new FormData();
          formData.append('Content-Type', selectedFile?.type);
          formData.append('file', selectedFile);

          const uploadImage = await axios.put(preSignedURL, selectedFile, {
            headers: {
              'content-type': selectedFile?.type,
              'Access-Control-Allow-Origin': '*',
            },
            onUploadProgress: (axiosProgressEvent) => {
              const progress = Math.round(
                (axiosProgressEvent.loaded / axiosProgressEvent.total) * 100
              );
              setUploadProgress(progress);
            },
          });

          // if upload image is successful
          if (uploadImage.status === 200) {
            onUpdatePropertyType(
              selectedPropertyType?.propertyTypeId,
              data?.name,
              storagePath
            );
          }
        }
      } else {
        onUpdatePropertyType(
          selectedPropertyType?.propertyTypeId,
          data?.name,
          data?.image
        );
      }
    } catch (error) {
      console.log({ error });
      toast.error(
        `${error?.response?.data?.message?.[0] ?? 'Something Went Wrong!'} `
      );
    }
  };

  const handleUploadImage = (e) => {
    const newImage = e.currentTarget.files;
    var allowedExtensions = /(\.svg|\.png|\.jpeg|\.webp)$/i;

    if (!newImage?.[0]?.name?.match(allowedExtensions)) {
      toast.error(
        `MediaType must be one of the following values: jpeg, jpg, png, svg, webp`
      );
      return false;
    }

    if (e.currentTarget?.files?.length === 1) {
      setSelectedFile(e.currentTarget.files?.[0]);
      setSelectedFileURL(URL.createObjectURL(e.currentTarget.files?.[0]));
      setValue('image', URL.createObjectURL(e.currentTarget.files?.[0]), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <Modal
      open={openModal}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onCloseModal();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      disableEscapeKeyDown
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 3,
        }}
      >
        <MDTypography id="modal-modal-title" variant="h4" component="h2">
          {isEdit ? 'Edit Property Type' : 'Add Property Type'}
        </MDTypography>
        {isLoading || (isEdit && !propertyTypeData) ? (
          <Box
            sx={{
              textAlign: 'center',
              paddingTop: '32px',
              paddingBottom: '24px',
            }}
          >
            <CircularProgress color="info" />
          </Box>
        ) : (
          <>
            <MDBox marginTop="32px">
              <MDInput
                {...register('name')}
                fullWidth
                label="Add Property Type Name"
                error={!!errors?.name}
                helperText={
                  errors?.name && (
                    <MDTypography color="error" variant="caption">
                      {errors?.name?.message}
                    </MDTypography>
                  )
                }
              ></MDInput>
              <MDButton
                sx={{ marginTop: 2 }}
                variant="outlined"
                color="info"
                startIcon={<FileUploadIcon />}
                component="label"
              >
                Upload
                <input
                  {...register('image')}
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={(e) => {
                    handleUploadImage(e);
                  }}
                />
              </MDButton>
              {selectedFileURL && (
                <CardMedia
                  sx={{
                    height: '40px',
                    width: '40px',
                    objectFit: 'cover',
                    display: 'inline',
                    margin: '0 0 0 16px',
                    verticalAlign: 'bottom',
                    borderRadius: '0',
                  }}
                  src={selectedFileURL}
                  component="img"
                />
              )}
            </MDBox>
            {uploadProgress !== 100 && (
              <MDBox align="center" marginTop="32px">
                <MDProgress value={uploadProgress} variant="determinate" />
              </MDBox>
            )}
            <MDBox align="right" marginTop="32px">
              <MDButton onClick={onCloseModal}>Discard</MDButton>
              {isEdit ? (
                <>
                  <MDButton
                    onClick={handleSubmit(onUpdatePropertyTypeSubmit)}
                    color="info"
                    disabled={
                      Object.keys(errors).length > 0 ||
                      !Object.keys(dirtyFields).length ||
                      !isValid ||
                      isSubmitting
                    }
                  >
                    Save
                  </MDButton>
                </>
              ) : (
                <MDButton
                  onClick={handleSubmit(onAddPropertyTypeSubmit)}
                  color="info"
                  disabled={
                    Object.keys(errors).length > 0 ||
                    !isValid ||
                    isSubmitting ||
                    !selectedFile
                  }
                >
                  Create
                </MDButton>
              )}
            </MDBox>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default AddPropertyType;
